import { useMutation } from "@apollo/client";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useEffect, useState } from "react";

import { CREATE_SUBSCRIPTION_CANCEL_PORTAL_SESSION } from "../../api/gqlQueries";
import { ReactComponent as Close } from "../../img/Close.svg";
import { ReactComponent as Sparkle } from "../../img/Sparkle.svg";

interface CancelBrilliantMaxModalProps {
  accessEndDate?: string;
  closeCancelBrilliantMaxModal: () => void;
  homeId: string;
  homeName: string;
  orgId: string;
  orgName: string;
}

const CancelBrilliantMaxModal: React.FC<CancelBrilliantMaxModalProps> = ({
  accessEndDate,
  closeCancelBrilliantMaxModal,
  homeId,
  homeName,
  orgId,
  orgName,
}) => {
  const [createSubscriptionCancelPortalSession] = useMutation(
    CREATE_SUBSCRIPTION_CANCEL_PORTAL_SESSION
  );

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    mixpanel.track("max_pro_cancel_modal_opened", {
      home_id: homeId,
      organization_id: orgId,
      organization_name: orgName,
    });
  }, []);

  const handleProceed = async () => {
    mixpanel.track("max_pro_cancel_confirmed", {
      home_id: homeId,
      organization_id: orgId,
      organization_name: orgName,
    });
    setHasError(false);
    // Open the tab synchronously inside the click handler so it isn't blocked
    // by the popup blocker, then point it at the URL once the mutation returns.
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createSubscriptionCancelPortalSession({
        variables: { homeId },
      });
      const url = data?.createSubscriptionCancelPortalSession?.portalUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setHasError(true);
      }
    } catch {
      newTab?.close();
      setHasError(true);
    }
  };

  return (
    <div className="popup-background">
      <div className="modal-create-container">
        <div className="modal-header">
          <div className="modal-title-container">
            <Sparkle
              className="modal-header-img cancel-bmx-modal-sparkle"
              title="Sparkle"
            />
            <div className="unit-title">
              Cancel Brilliant Max:{" "}
              <span className="cancel-bmx-modal-title-name">{homeName}</span>
            </div>
          </div>
          <Close
            className="modal-header-img"
            onClick={closeCancelBrilliantMaxModal}
            title="Close modal"
          />
        </div>

        <div className="modal-create-body">
          <div className="cancel-bmx-modal-info-card">
            Once confirmed, this home&apos;s subscription will remain active
            until the end of the current billing period.
          </div>

          <div className="cancel-bmx-modal-table margin-top-small">
            <span className="cancel-bmx-modal-table-header sub-heading">
              Home Name
            </span>
            <span className="cancel-bmx-modal-table-header sub-heading">
              Access will end on
            </span>
            <span className="cancel-bmx-modal-table-value tool-tip">
              {homeName}
            </span>
            <span className="cancel-bmx-modal-table-date tool-tip">
              {accessEndDate ? moment(accessEndDate).format("LL") : "—"}
            </span>
          </div>

          {hasError && (
            <p className="cancel-bmx-modal-error">
              Something went wrong. Please try again.
            </p>
          )}

          <div className="cancel-bmx-modal-actions margin-top-small">
            <button
              className="tertiary-btn-small tertiary-btn-small-alert"
              type="button"
              onClick={closeCancelBrilliantMaxModal}
            >
              Go Back
            </button>
            <button
              className="tertiary-btn-small tertiary-btn-small-main tertiary-btn-small-main-alert"
              type="button"
              onClick={handleProceed}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelBrilliantMaxModal;
